import React, { useEffect, useState, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  ChevronLeft,
  ChevronRight,
  PlusCircle,
  Info,
  BookOpen,
  Link2,
  XCircle,
  FileText,
  HelpCircle,
  PlayCircle,
  Trash2,
  GripVertical,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit3,
  Upload,
  Plus,
  MoreVertical,
  Image,
  Subtitles,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getModulesByCourseId, updateModuleOrder, updateModule, deleteModule } from "../../State/Module/Action";
import { getChaptersByModuleId, deleteChapter, reuploadVideoInit, updateArticle, getArticleByChapterId, clearArticleContent, deleteArticle } from "../../State/Chapter/Action";
import { deleteAllQuizzesFromChapter, upsertQuizzesForChapter, getQuizzesByChapter } from "../../State/Quiz/Action";
import { getCourseById } from "../../State/Course/Action";
import ReOrderChapter from "../Chapter/ReOrderChapter";
import ReOrderModule from "./ReOrderModule";
import * as tus from "tus-js-client";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  LinearProgress,
  Paper,
  Menu,
} from "@mui/material";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import api from "../../services/api";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ModuleList = () => {
  const dispatch = useDispatch();
  const { courseId } = useParams();

  const { moduleListReponse, updateModuleLoading, updateModuleError, updateModuleResult, deleteModuleLoading, deleteModuleError, deleteModuleResult } = useSelector((store) => store.module);
  const { 
    chapterListResponse, 
    reuploadVideoInitDetails, 
    reuploadVideoInitLoading, 
    reuploadVideoInitError,
    updateArticleResult,
    updateArticleLoading,
    updateArticleError,
    articleByChapterId,
    articleByChapterIdLoading,
    articleByChapterIdError,
    deleteArticleResult,
    deleteArticleLoading,
    deleteArticleError
  } = useSelector((store) => store.chapter);
  const { course } = useSelector((store) => store.course);
  const { 
    deleteAllQuizzesFromChapterResult,
    loadingDeleteAllQuizzes,
    errorDeleteAllQuizzes,
    upsertQuizzesForChapterResult,
    loadingUpsertQuizzes,
    errorUpsertQuizzes,
    quizzesByChapter,
    loadingQuizzesByChapter,
    errorQuizzesByChapter
  } = useSelector((store) => store.quiz);

  const [selectedModule, setSelectedModule] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isLoadingChapters, setIsLoadingChapters] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chapterToDelete, setChapterToDelete] = useState(null);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [reorderModuleDialogOpen, setReorderModuleDialogOpen] = useState(false);
  const [reuploadDialogOpen, setReuploadDialogOpen] = useState(false);
  const [selectedChapterForReupload, setSelectedChapterForReupload] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmReuploadDialogOpen, setConfirmReuploadDialogOpen] = useState(false);
  const [chapterToReupload, setChapterToReupload] = useState(null);
  const [updateArticleDialogOpen, setUpdateArticleDialogOpen] = useState(false);
  const [selectedChapterForArticle, setSelectedChapterForArticle] = useState(null);
  const [articleContent, setArticleContent] = useState("");
  const [deleteArticleDialogOpen, setDeleteArticleDialogOpen] = useState(false);
  const [chapterToDeleteArticle, setChapterToDeleteArticle] = useState(null);
  const [deleteQuizDialogOpen, setDeleteQuizDialogOpen] = useState(false);
  const [chapterToDeleteQuiz, setChapterToDeleteQuiz] = useState(null);
  const [updateQuizDialogOpen, setUpdateQuizDialogOpen] = useState(false);
  const [selectedChapterForQuiz, setSelectedChapterForQuiz] = useState(null);
  
  // Quiz form state variables for multiple questions
  const [quizzes, setQuizzes] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [validationError, setValidationError] = useState("");
  
  // Edit Module state variables
  const [editModuleDialogOpen, setEditModuleDialogOpen] = useState(false);
  const [moduleToEdit, setModuleToEdit] = useState(null);
  const [editModuleName, setEditModuleName] = useState("");
  const [editModuleDescription, setEditModuleDescription] = useState("");
  
  // Delete Module state variables
  const [deleteModuleDialogOpen, setDeleteModuleDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  
  // Chapter menu dropdown state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedChapterForMenu, setSelectedChapterForMenu] = useState(null);
  
  // Thumbnail upload state
  const [thumbnailUploadDialogOpen, setThumbnailUploadDialogOpen] = useState(false);
  const [selectedChapterForThumbnail, setSelectedChapterForThumbnail] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  
  // Caption state
  const [captionDialogOpen, setCaptionDialogOpen] = useState(false);
  const [selectedChapterForCaption, setSelectedChapterForCaption] = useState(null);
  const [captionFile, setCaptionFile] = useState(null);
  const [captionUploading, setCaptionUploading] = useState(false);
  const [captionLanguage, setCaptionLanguage] = useState('en');
  const [captionLabel, setCaptionLabel] = useState('');
  
  const navigate = useNavigate();

  const selectModule = useCallback((module) => {
    // Prevent rapid API calls if already loading or same module selected
    if (isLoadingChapters || selectedModule?.id === module.id) {
      return;
    }
    
    setSelectedModule(module);
    setIsLoadingChapters(true);
    dispatch(getChaptersByModuleId(module.id));
  }, [dispatch, isLoadingChapters, selectedModule]);

  useEffect(() => {
    if (courseId) {
      dispatch(getModulesByCourseId(courseId));
      dispatch(getCourseById(courseId));
    }
  }, [courseId, dispatch]);

  useEffect(() => {
    if (moduleListReponse?.modules?.length && !selectedModule) {
      selectModule(moduleListReponse.modules[0]);
    }
  }, [moduleListReponse, selectModule, selectedModule]);

  useEffect(() => {
    if (chapterListResponse?.chapters) {
      setChapters(chapterListResponse.chapters);
      setIsLoadingChapters(false);
    }
  }, [chapterListResponse]);

  // Handle reupload video init loading and errors
  useEffect(() => {
    if (reuploadVideoInitError) {
      setErrorMessage("Error initializing video reupload: " + reuploadVideoInitError);
      setErrorDialogOpen(true);
    }
  }, [reuploadVideoInitError]);

  // Handle article update success and errors
  useEffect(() => {
    if (updateArticleResult && Object.keys(updateArticleResult).length > 0) {
      setUpdateArticleDialogOpen(false);
      setSelectedChapterForArticle(null);
      setArticleContent("");
      setErrorMessage("Article has been updated successfully! The chapter content has been saved.");
      setSuccessDialogOpen(true);
      
      // Refresh chapters to show updated content
      if (selectedModule) {
        dispatch(getChaptersByModuleId(selectedModule.id));
      }
      
      // Clear the success result to prevent re-triggering
      dispatch({ type: 'UPDATE_ARTICLE_SUCCESS', payload: {} });
    }
  }, [updateArticleResult, selectedModule, dispatch]);

  useEffect(() => {
    if (updateArticleError) {
      setErrorMessage("Failed to update article: " + updateArticleError);
      setErrorDialogOpen(true);
    }
  }, [updateArticleError]);

  // Handle article content fetching success and errors
  useEffect(() => {
    if (articleByChapterId && selectedChapterForArticle) {
      setArticleContent(articleByChapterId);
    }
  }, [articleByChapterId, selectedChapterForArticle]);

  useEffect(() => {
    if (articleByChapterIdError) {
      setErrorMessage("Failed to load article content: " + articleByChapterIdError);
      setErrorDialogOpen(true);
      setUpdateArticleDialogOpen(false);
    }
  }, [articleByChapterIdError]);

  // Handle delete article success and errors
  useEffect(() => {
    if (deleteArticleResult && Object.keys(deleteArticleResult).length > 0) {
      setErrorMessage("Article has been deleted successfully! The chapter content has been removed.");
      setSuccessDialogOpen(true);
      
      // Refresh chapters to show updated content
      if (selectedModule) {
        dispatch(getChaptersByModuleId(selectedModule.id));
      }
      
      // Clear the success result to prevent re-triggering
      dispatch({ type: 'DELETE_ARTICLE_SUCCESS', payload: {} });
    }
  }, [deleteArticleResult, selectedModule, dispatch]);

  useEffect(() => {
    if (deleteArticleError) {
      setErrorMessage("Failed to delete article: " + deleteArticleError);
      setErrorDialogOpen(true);
    }
  }, [deleteArticleError]);

  // Handle delete all quizzes success and errors
  useEffect(() => {
    if (deleteAllQuizzesFromChapterResult && Object.keys(deleteAllQuizzesFromChapterResult).length > 0) {
      setErrorMessage("All quizzes have been deleted successfully! The quiz content has been removed from the chapter.");
      setSuccessDialogOpen(true);
      
      // Refresh chapters to show updated content
      if (selectedModule) {
        dispatch(getChaptersByModuleId(selectedModule.id));
      }
      
      // Clear the success result to prevent re-triggering
      dispatch({ type: 'DELETE_ALL_QUIZZES_FROM_CHAPTER_SUCCESS', payload: {} });
    }
  }, [deleteAllQuizzesFromChapterResult, selectedModule, dispatch]);

  useEffect(() => {
    if (errorDeleteAllQuizzes) {
      setErrorMessage("Failed to delete quizzes: " + errorDeleteAllQuizzes);
      setErrorDialogOpen(true);
    }
  }, [errorDeleteAllQuizzes]);

  // Handle quiz update success and errors
  useEffect(() => {
    if (upsertQuizzesForChapterResult && Object.keys(upsertQuizzesForChapterResult).length > 0) {
      setUpdateQuizDialogOpen(false);
      setSelectedChapterForQuiz(null);
      setErrorMessage("Quizzes have been updated successfully! The chapter content has been saved.");
      setSuccessDialogOpen(true);
      
      // Refresh chapters to show updated content
      if (selectedModule) {
        dispatch(getChaptersByModuleId(selectedModule.id));
      }
      
      // Clear the success result to prevent re-triggering
      dispatch({ type: 'UPSERT_QUIZZES_FOR_CHAPTER_SUCCESS', payload: {} });
    }
  }, [upsertQuizzesForChapterResult, selectedModule, dispatch]);

  useEffect(() => {
    if (errorUpsertQuizzes) {
      setErrorMessage("Failed to update quizzes: " + errorUpsertQuizzes);
      setErrorDialogOpen(true);
    }
  }, [errorUpsertQuizzes]);

  // Handle quiz fetching errors
  useEffect(() => {
    if (errorQuizzesByChapter) {
      console.log("Quiz fetching error:", errorQuizzesByChapter);
      setErrorMessage("Failed to fetch quizzes: " + errorQuizzesByChapter);
      setErrorDialogOpen(true);
    }
  }, [errorQuizzesByChapter]);

  // Debug Redux state
  useEffect(() => {
    console.log("Quiz Redux state:", {
      quizzesByChapter,
      loadingQuizzesByChapter,
      errorQuizzesByChapter
    });
  }, [quizzesByChapter, loadingQuizzesByChapter, errorQuizzesByChapter]);

  // Load existing quizzes when dialog opens
  useEffect(() => {
    if (updateQuizDialogOpen && selectedChapterForQuiz) {
      console.log("Loading quizzes for chapter:", selectedChapterForQuiz.id);
      dispatch(getQuizzesByChapter(selectedChapterForQuiz.id));
    }
  }, [updateQuizDialogOpen, selectedChapterForQuiz, dispatch]);

  // Populate form with existing quiz data
  useEffect(() => {
    console.log("quizzesByChapter changed:", quizzesByChapter);
    if (quizzesByChapter && quizzesByChapter.length > 0) {
      console.log("Setting quizzes data:", quizzesByChapter);
      setQuizzes(quizzesByChapter);
      setCurrentQuestionIndex(0);
    } else if (quizzesByChapter && quizzesByChapter.length === 0) {
      console.log("No existing quizzes, resetting form");
      setQuizzes([]);
      setCurrentQuestionIndex(0);
    }
  }, [quizzesByChapter]);

  // Handle module update success
  useEffect(() => {
    if (updateModuleResult && updateModuleResult.success) {
      setEditModuleDialogOpen(false);
      setModuleToEdit(null);
      setEditModuleName("");
      setEditModuleDescription("");
      setErrorMessage(updateModuleResult.message || "Module has been updated successfully!");
      setSuccessDialogOpen(true);
      
      // Refresh modules to show updated content
      if (courseId) {
        dispatch(getModulesByCourseId(courseId));
      }
      
      // Clear the success result to prevent re-triggering
      dispatch({ type: 'UPDATE_MODULE_SUCCESS', payload: null });
    }
  }, [updateModuleResult, courseId, dispatch]);

  // Handle module update error
  useEffect(() => {
    if (updateModuleError) {
      const errorMsg = typeof updateModuleError === 'string' 
        ? updateModuleError 
        : updateModuleError.message || JSON.stringify(updateModuleError);
      setErrorMessage("Failed to update module: " + errorMsg);
      setErrorDialogOpen(true);
    }
  }, [updateModuleError]);

  // Handle module delete success
  useEffect(() => {
    if (deleteModuleResult && deleteModuleResult.success) {
      setDeleteModuleDialogOpen(false);
      setModuleToDelete(null);
      setErrorMessage(deleteModuleResult.message || "Module has been deleted successfully!");
      setSuccessDialogOpen(true);
      
      // Refresh modules to show updated list
      if (courseId) {
        dispatch(getModulesByCourseId(courseId));
      }
      
      // If the deleted module was selected, clear selection
      if (selectedModule && moduleToDelete && selectedModule.id === moduleToDelete.id) {
        setSelectedModule(null);
        setChapters([]);
      }
      
      // Clear the success result to prevent re-triggering
      dispatch({ type: 'DELETE_MODULE_SUCCESS', payload: null });
    }
  }, [deleteModuleResult, courseId, dispatch, selectedModule, moduleToDelete]);

  // Handle module delete error
  useEffect(() => {
    if (deleteModuleError) {
      const errorMsg = typeof deleteModuleError === 'string' 
        ? deleteModuleError 
        : deleteModuleError.message || JSON.stringify(deleteModuleError);
      setErrorMessage("Failed to delete module: " + errorMsg);
      setErrorDialogOpen(true);
    }
  }, [deleteModuleError]);

  const handleResize = (e) => {
    const newWidth = e.clientX;
    if (newWidth >= 200 && newWidth <= 500) {
      setSidebarWidth(newWidth);
    }
  };

  const handleMouseDown = () => {
    window.addEventListener("mousemove", handleResize);
    window.addEventListener(
      "mouseup",
      () => window.removeEventListener("mousemove", handleResize),
      { once: true }
    );
  };

  const handleChapterClick = (chapter) => {
    setSelectedChapter(chapter);
    if (chapter.type === "VIDEO") {
      setOpenDialog(true);
    } else if (chapter.type === "ARTICLE") {
      navigate(`/show-article/${chapter.id}`);
    } else if (chapter.type === "QUIZ") {
      navigate(`/play-quiz/${chapter.id}?moduleId=${selectedModule?.id}`);
    } else {
      navigate(`/chapter/${selectedModule?.id}`);
    }
  };

  const getIconByChapterType = (type) => {
    switch (type) {
      case "VIDEO":
        return <PlayCircle size={20} className="text-indigo-600" />;
      case "QUIZ":
        return <HelpCircle size={20} className="text-purple-600" />;
      case "ARTICLE":
        return <FileText size={20} className="text-green-600" />;
      default:
        return <Info size={20} />;
    }
  };

  const getVideoUploadStatusIcon = (videoUploadStatusCode) => {
    switch (videoUploadStatusCode) {
      case 0: // QUEUED
        return <Clock size={16} className="text-yellow-600" />;
      case 3: // FINISHED
        return <CheckCircle size={16} className="text-green-600" />;
      case 5: // FAILED
        return <AlertCircle size={16} className="text-red-600" />;
      default: // IN_PROGRESS
        return <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>;
    }
  };

  const getVideoUploadStatusTooltip = (videoUploadStatusCode) => {
    switch (videoUploadStatusCode) {
      case 0:
        return "Upload Queued";
      case 3:
        return "Upload Completed";
      case 5:
        return "Upload Failed";
      default:
        return "Upload In Progress";
    }
  };

  const handleDeleteClick = (e, chapter) => {
    e.stopPropagation(); // Prevent chapter click event
    
    if (chapter.type === "ARTICLE") {
      // For article chapters, use dialog confirmation
      setChapterToDeleteArticle(chapter);
      setDeleteArticleDialogOpen(true);
    } else if (chapter.type === "QUIZ") {
      // For quiz chapters, use dialog confirmation
      setChapterToDeleteQuiz(chapter);
      setDeleteQuizDialogOpen(true);
    } else {
      // For other chapters (VIDEO), use the existing dialog flow
      setChapterToDelete(chapter);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (chapterToDelete) {
      dispatch(deleteChapter(chapterToDelete.id));
      setDeleteDialogOpen(false);
      setChapterToDelete(null);
    }
  };

  const handleReuploadVideo = (chapter) => {
    setChapterToReupload(chapter);
    setConfirmReuploadDialogOpen(true);
  };

  const handleConfirmReupload = () => {
    if (chapterToReupload) {
      setSelectedChapterForReupload(chapterToReupload);
      setReuploadDialogOpen(true);
      setConfirmReuploadDialogOpen(false);
      setChapterToReupload(null);
      // Call Redux action to initialize video reupload
      dispatch(reuploadVideoInit(chapterToReupload.id));
    }
  };

  const handleCancelReupload = () => {
    setConfirmReuploadDialogOpen(false);
    setChapterToReupload(null);
  };

  const handleUpdateArticle = (chapter) => {
    setSelectedChapterForArticle(chapter);
    setArticleContent(""); // Clear previous content
    setUpdateArticleDialogOpen(true);
    // Fetch article content from server
    dispatch(getArticleByChapterId(chapter.id));
  };

  const handleArticleContentChange = (content) => {
    setArticleContent(content);
  };

  const handleArticleSave = () => {
    if (selectedChapterForArticle && articleContent && articleContent.trim() !== '' && articleContent !== '<p><br></p>') {
      dispatch(updateArticle(selectedChapterForArticle.id, articleContent));
    }
  };

  const handleArticleCancel = () => {
    setUpdateArticleDialogOpen(false);
    setSelectedChapterForArticle(null);
    setArticleContent("");
    // Clear the article content from Redux state
    dispatch(clearArticleContent());
  };


  const handleVideoUpload = (file) => {
    if (!reuploadVideoInitDetails || !file) return;

    // Check file size (limit to 2GB)
    const maxFileSize = 2 * 1024 * 1024 * 1024; // 2GB in bytes
    if (file.size > maxFileSize) {
      setErrorMessage(`File size (${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB) exceeds the maximum allowed size of 2GB. Please choose a smaller file.`);
      setErrorDialogOpen(true);
      return;
    }

    // Check file type
    if (!file.type.startsWith('video/')) {
      setErrorMessage("Please select a valid video file.");
      setErrorDialogOpen(true);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    // Validate the authorization expiry timestamp
    const expiryTime = new Date(parseInt(reuploadVideoInitDetails.authorizationExpiry) * 1000);
    const currentTime = new Date();
    const timeUntilExpiry = expiryTime.getTime() - currentTime.getTime();
    
    if (timeUntilExpiry < 0) {
      setErrorMessage("Upload authorization has expired. Please try uploading again.");
      setErrorDialogOpen(true);
      setIsUploading(false);
      return;
    }

    // Start TUS upload with same implementation as Chapter.jsx
    var upload = new tus.Upload(file, {
      endpoint: reuploadVideoInitDetails.tusUploadUrl,
      retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
      metadata: {
        filetype: file.type,
        filename: file.name,
        collection: reuploadVideoInitDetails.collectionId,
      },
      headers: {
        AuthorizationSignature: reuploadVideoInitDetails.authorizationSignature,
        AuthorizationExpire: reuploadVideoInitDetails.authorizationExpiry,
        LibraryId: reuploadVideoInitDetails.libraryId,
        VideoId: reuploadVideoInitDetails.videoId,
      },
      onError: (error) => {
        console.error("Upload failed:", error);
        setIsUploading(false);
        setUploadProgress(0);
        
        // Enhanced error handling with detailed logging (same as Chapter.jsx)
        let errorMessage = "Upload failed. ";
        
        if (error.originalResponse) {
          const status = error.originalResponse.getStatus();
          const responseBody = error.originalResponse.getBody();
          
          if (status === 401) {
            errorMessage += "Authentication failed (401). This could be due to: ";
            errorMessage += "1) Invalid or expired authorization signature, ";
            errorMessage += "2) Incorrect library ID or video ID, ";
            errorMessage += "3) API key permissions issue. ";
            errorMessage += "Please check the console logs for details and try uploading again.";
          } else if (status === 413) {
            errorMessage += "File too large. Please try with a smaller file.";
          } else if (status >= 500) {
            errorMessage += "Server error. Please try again later.";
          } else {
            errorMessage += `Server returned status ${status}. Response: ${responseBody || 'No response body'}`;
          }
        } else {
          errorMessage += error.message || "Unknown error occurred.";
        }
        
        setErrorMessage(errorMessage);
        setErrorDialogOpen(true);
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const progressPercent = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
        setUploadProgress(parseFloat(progressPercent));
      },
      onSuccess: async () => {
        setIsUploading(false);
        setUploadProgress(100);

        // Close upload dialog immediately
        setReuploadDialogOpen(false);
        setSelectedChapterForReupload(null);
        setUploadProgress(0);

        // Notify backend upload is done (same as Chapter.jsx)
        try {
          api.fetch(`/chapters/${reuploadVideoInitDetails.chapterId}/finalize-video-upload`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          });
        } catch (error) {
          console.error("Failed to finalize upload:", error);
        }

        // Refresh chapters to show updated video
        if (selectedModule) {
          dispatch(getChaptersByModuleId(selectedModule.id));
        }
        
        // Show success dialog with video-specific message
        setErrorMessage("Video has been uploaded successfully! The chapter has been updated with the new video.");
        setSuccessDialogOpen(true);
      },
    });

    // Check if there are any previous uploads to continue (same as Chapter.jsx)
    upload.findPreviousUploads().then(function (previousUploads) {
      // Found previous uploads so we select the first one.
      if (previousUploads.length) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }

      // Start the upload
      upload.start();
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setChapterToDelete(null);
  };

  const handleDeleteArticleConfirm = () => {
    if (chapterToDeleteArticle) {
      dispatch(deleteArticle(chapterToDeleteArticle.id));
      setDeleteArticleDialogOpen(false);
      setChapterToDeleteArticle(null);
    }
  };

  const handleDeleteArticleCancel = () => {
    setDeleteArticleDialogOpen(false);
    setChapterToDeleteArticle(null);
  };

  const handleDeleteQuizConfirm = () => {
    if (chapterToDeleteQuiz) {
      dispatch(deleteAllQuizzesFromChapter(chapterToDeleteQuiz.id));
      setDeleteQuizDialogOpen(false);
      setChapterToDeleteQuiz(null);
    }
  };

  const handleDeleteQuizCancel = () => {
    setDeleteQuizDialogOpen(false);
    setChapterToDeleteQuiz(null);
  };

  // Quiz form helper functions for multiple questions
  const resetQuizForm = () => {
    setQuizzes([]);
    setCurrentQuestionIndex(0);
    setValidationError("");
  };

  const getCurrentQuiz = () => {
    return quizzes[currentQuestionIndex] || null;
  };

  const updateCurrentQuiz = (field, value) => {
    if (!quizzes[currentQuestionIndex]) return;
    
    const updatedQuizzes = [...quizzes];
    updatedQuizzes[currentQuestionIndex] = {
      ...updatedQuizzes[currentQuestionIndex],
      [field]: value
    };
    setQuizzes(updatedQuizzes);
  };

  const handleOptionChange = (optionIndex, value) => {
    const optionLetter = String.fromCharCode(65 + optionIndex); // A, B, C, D, E
    updateCurrentQuiz(`option${optionLetter}`, value.slice(0, 128));
  };

  const handleCorrectChange = (optionIndex) => {
    const currentQuiz = getCurrentQuiz();
    if (!currentQuiz) return;

    const optionLetter = String.fromCharCode(65 + optionIndex); // A, B, C, D, E
    const correctField = `option${optionLetter}Correct`;
    
    if (currentQuiz.multipleAnswers) {
      // Toggle for multiple answers
      updateCurrentQuiz(correctField, !currentQuiz[correctField]);
    } else {
      // Set only this one as correct for single answer
      const updatedQuiz = { ...currentQuiz };
      ['optionACorrect', 'optionBCorrect', 'optionCCorrect', 'optionDCorrect', 'optionECorrect'].forEach(field => {
        updatedQuiz[field] = false;
      });
      updatedQuiz[correctField] = true;
      
      const updatedQuizzes = [...quizzes];
      updatedQuizzes[currentQuestionIndex] = updatedQuiz;
      setQuizzes(updatedQuizzes);
    }
  };

  const addOption = () => {
    const currentQuiz = getCurrentQuiz();
    if (!currentQuiz) return;

    const currentOptions = [
      currentQuiz.optionA || "",
      currentQuiz.optionB || "",
      currentQuiz.optionC || "",
      currentQuiz.optionD || "",
      currentQuiz.optionE || ""
    ].filter(opt => opt !== "");

    if (currentOptions.length < 5) {
      const nextOptionLetter = String.fromCharCode(65 + currentOptions.length); // A, B, C, D, E
      updateCurrentQuiz(`option${nextOptionLetter}`, "");
      updateCurrentQuiz(`option${nextOptionLetter}Correct`, false);
    }
  };

  const deleteOption = (optionIndex) => {
    const currentQuiz = getCurrentQuiz();
    if (!currentQuiz) return;

    const currentOptions = [
      currentQuiz.optionA || "",
      currentQuiz.optionB || "",
      currentQuiz.optionC || "",
      currentQuiz.optionD || "",
      currentQuiz.optionE || ""
    ].filter(opt => opt !== "");

    if (currentOptions.length > 2) {
      const optionLetter = String.fromCharCode(65 + optionIndex); // A, B, C, D, E
      updateCurrentQuiz(`option${optionLetter}`, "");
      updateCurrentQuiz(`option${optionLetter}Correct`, false);
    }
  };

  const navigateToQuestion = (index) => {
    if (index >= 0 && index < quizzes.length) {
      setCurrentQuestionIndex(index);
      setValidationError("");
    }
  };

  const addNewQuestion = () => {
    const newQuiz = {
      id: `temp-${Date.now()}`, // Temporary ID for new questions
      question: "",
      optionA: "",
      optionB: "",
      optionC: "",
      optionD: "",
      optionE: "",
      optionACorrect: false,
      optionBCorrect: false,
      optionCCorrect: false,
      optionDCorrect: false,
      optionECorrect: false,
      explanation: "",
      multipleAnswers: false
    };
    
    setQuizzes([...quizzes, newQuiz]);
    setCurrentQuestionIndex(quizzes.length);
  };

  const deleteCurrentQuestion = () => {
    if (quizzes.length > 1) {
      const updatedQuizzes = quizzes.filter((_, index) => index !== currentQuestionIndex);
      setQuizzes(updatedQuizzes);
      
      // Adjust current question index
      if (currentQuestionIndex >= updatedQuizzes.length) {
        setCurrentQuestionIndex(updatedQuizzes.length - 1);
      }
    }
  };

  const handleUpdateQuiz = (chapter) => {
    setSelectedChapterForQuiz(chapter);
    setUpdateQuizDialogOpen(true);
  };

  const handleQuizUpdateConfirm = () => {
    if (selectedChapterForQuiz && quizzes.length > 0) {
      // Validate all quizzes
      for (let i = 0; i < quizzes.length; i++) {
        const quiz = quizzes[i];
        const hasCorrectAnswer = quiz.optionACorrect || quiz.optionBCorrect || 
                                quiz.optionCCorrect || quiz.optionDCorrect || quiz.optionECorrect;
        
        if (!hasCorrectAnswer) {
          setValidationError(`Question ${i + 1}: Choose at least one correct option`);
          setCurrentQuestionIndex(i);
          return;
        }
        
        if (!quiz.question.trim()) {
          setValidationError(`Question ${i + 1}: Please enter a question`);
          setCurrentQuestionIndex(i);
          return;
        }
      }
      
      setValidationError("");

      // Create quiz data from all quizzes
      const quizData = {
        quizzes: quizzes.map(quiz => ({
          chapterId: selectedChapterForQuiz.id,
          question: quiz.question,
          optionA: quiz.optionA || "",
          optionB: quiz.optionB || "",
          optionC: quiz.optionC || "",
          optionD: quiz.optionD || "",
          optionE: quiz.optionE || "",
          multipleAnswers: quiz.multipleAnswers,
          optionACorrect: quiz.optionACorrect || false,
          optionBCorrect: quiz.optionBCorrect || false,
          optionCCorrect: quiz.optionCCorrect || false,
          optionDCorrect: quiz.optionDCorrect || false,
          optionECorrect: quiz.optionECorrect || false,
          explanation: quiz.explanation || "",
        }))
      };
      
      dispatch(upsertQuizzesForChapter(selectedChapterForQuiz.id, quizData));
    }
  };

  const handleQuizUpdateCancel = () => {
    setUpdateQuizDialogOpen(false);
    setSelectedChapterForQuiz(null);
    resetQuizForm();
  };

  const handleReorderSuccess = () => {
    // Refresh chapters after successful reorder
    if (selectedModule?.id) {
      dispatch(getChaptersByModuleId(selectedModule.id));
    }
  };

  const handleReorderModuleSuccess = () => {
    // Refresh modules after reorder
    if (courseId) {
      dispatch(getModulesByCourseId(courseId));
    }
    setReorderModuleDialogOpen(false);
  };

  const handleEditModule = (e, module) => {
    e.stopPropagation(); // Prevent module click event
    setModuleToEdit(module);
    setEditModuleName(module.title || "");
    setEditModuleDescription(module.description || "");
    setEditModuleDialogOpen(true);
  };

  const handleModuleUpdateSave = () => {
    if (moduleToEdit && editModuleName.trim()) {
      const moduleData = {
        name: editModuleName.trim(),
        description: editModuleDescription.trim(),
      };
      dispatch(updateModule(moduleToEdit.id, moduleData));
    }
  };

  const handleModuleUpdateCancel = () => {
    setEditModuleDialogOpen(false);
    setModuleToEdit(null);
    setEditModuleName("");
    setEditModuleDescription("");
  };

  const handleDeleteModule = (e, module) => {
    e.stopPropagation(); // Prevent module click event
    setModuleToDelete(module);
    setDeleteModuleDialogOpen(true);
  };

  const handleDeleteModuleConfirm = () => {
    if (moduleToDelete) {
      dispatch(deleteModule(moduleToDelete.id));
    }
  };

  const handleDeleteModuleCancel = () => {
    setDeleteModuleDialogOpen(false);
    setModuleToDelete(null);
  };

  // Menu handlers for chapter settings dropdown
  const handleMenuOpen = (event, chapter) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedChapterForMenu(chapter);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedChapterForMenu(null);
  };

  const handleMenuUpdateArticle = () => {
    handleMenuClose();
    if (selectedChapterForMenu) {
      handleUpdateArticle(selectedChapterForMenu);
    }
  };

  const handleMenuReuploadVideo = () => {
    handleMenuClose();
    if (selectedChapterForMenu) {
      handleReuploadVideo(selectedChapterForMenu);
    }
  };

  const handleMenuUpdateQuiz = () => {
    handleMenuClose();
    if (selectedChapterForMenu) {
      handleUpdateQuiz(selectedChapterForMenu);
    }
  };

  const handleMenuUploadThumbnail = () => {
    handleMenuClose();
    if (selectedChapterForMenu) {
      setSelectedChapterForThumbnail(selectedChapterForMenu);
      setThumbnailFile(null);
      setThumbnailUploadDialogOpen(true);
    }
  };

  const handleThumbnailFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrorMessage("Please select a valid image file.");
        setErrorDialogOpen(true);
        return;
      }
      // Validate file size (limit to 5MB)
      const maxFileSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxFileSize) {
        setErrorMessage(`File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds the maximum allowed size of 5MB.`);
        setErrorDialogOpen(true);
        return;
      }
      setThumbnailFile(file);
    }
  };

  const handleThumbnailUpload = async () => {
    if (!selectedChapterForThumbnail || !thumbnailFile) {
      setErrorMessage("Please select a thumbnail image.");
      setErrorDialogOpen(true);
      return;
    }

    setThumbnailUploading(true);

    try {
      const formData = new FormData();
      formData.append('thumbnail', thumbnailFile);

      const response = await api.post(
        `/chapters/${selectedChapterForThumbnail.id}/thumbnail`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setThumbnailUploadDialogOpen(false);
      setSelectedChapterForThumbnail(null);
      setThumbnailFile(null);
      setErrorMessage("Thumbnail uploaded successfully!");
      setSuccessDialogOpen(true);

      // Refresh chapters to show updated thumbnail
      if (selectedModule) {
        dispatch(getChaptersByModuleId(selectedModule.id));
      }
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      setErrorMessage(error.response?.data?.message || "Failed to upload thumbnail. Please try again.");
      setErrorDialogOpen(true);
    } finally {
      setThumbnailUploading(false);
    }
  };

  const handleThumbnailUploadCancel = () => {
    setThumbnailUploadDialogOpen(false);
    setSelectedChapterForThumbnail(null);
    setThumbnailFile(null);
  };

  const handleMenuAddCaption = () => {
    handleMenuClose();
    if (selectedChapterForMenu) {
      setSelectedChapterForCaption(selectedChapterForMenu);
      setCaptionFile(null);
      setCaptionLanguage('en');
      setCaptionLabel('');
      setCaptionDialogOpen(true);
    }
  };

  const handleCaptionFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type (VTT, SRT files)
      const validTypes = ['.vtt', '.srt'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validTypes.includes(fileExtension)) {
        setErrorMessage("Please select a valid caption file (.vtt or .srt).");
        setErrorDialogOpen(true);
        return;
      }
      
      // Validate file size (limit to 2MB)
      const maxFileSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxFileSize) {
        setErrorMessage(`File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds the maximum allowed size of 2MB.`);
        setErrorDialogOpen(true);
        return;
      }
      setCaptionFile(file);
    }
  };

  const handleCaptionUpload = async () => {
    if (!selectedChapterForCaption || !captionFile) {
      setErrorMessage("Please select a caption file.");
      setErrorDialogOpen(true);
      return;
    }

    if (!captionLabel.trim()) {
      setErrorMessage("Please enter a label for the caption.");
      setErrorDialogOpen(true);
      return;
    }

    setCaptionUploading(true);

    try {
      const formData = new FormData();
      formData.append('captionFile', captionFile);
      formData.append('srclang', captionLanguage);
      formData.append('label', captionLabel);

      const response = await api.post(
        `/chapters/${selectedChapterForCaption.id}/caption`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setCaptionDialogOpen(false);
      setSelectedChapterForCaption(null);
      setCaptionFile(null);
      setCaptionLanguage('en');
      setCaptionLabel('');
      setErrorMessage("Caption uploaded successfully!");
      setSuccessDialogOpen(true);

      // Refresh chapters to show updated caption
      if (selectedModule) {
        dispatch(getChaptersByModuleId(selectedModule.id));
      }
    } catch (error) {
      console.error("Error uploading caption:", error);
      setErrorMessage(error.response?.data?.message || "Failed to upload caption. Please try again.");
      setErrorDialogOpen(true);
    } finally {
      setCaptionUploading(false);
    }
  };

  const handleCaptionUploadCancel = () => {
    setCaptionDialogOpen(false);
    setSelectedChapterForCaption(null);
    setCaptionFile(null);
    setCaptionLanguage('en');
    setCaptionLabel('');
  };

  const handleMenuEditChapter = () => {
    handleMenuClose();
    if (selectedChapterForMenu) {
      console.log("Edit button clicked, navigating to:", `/edit-chapter-info/${selectedChapterForMenu.id}`);
      window.location.href = `/edit-chapter-info/${selectedChapterForMenu.id}`;
    }
  };

  const handleMenuDelete = (e) => {
    handleMenuClose();
    if (selectedChapterForMenu) {
      handleDeleteClick(e, selectedChapterForMenu);
    }
  };






  return (
    <>
      <div className="flex h-screen overflow-hidden relative font-sans bg-gray-50">
        {isSidebarVisible && (
          <div
            className="bg-white border-r border-gray-200 text-gray-800 transition-all duration-300 shadow-2xl z-10 backdrop-blur-sm"
            style={{ width: `${sidebarWidth}px` }}
          >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50">
              <h2 className="text-xl font-bold flex items-center gap-3 text-gray-800">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <BookOpen size={20} className="text-indigo-600" />
                </div>
                Modules
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setReorderModuleDialogOpen(true)}
                  disabled={!moduleListReponse?.modules || moduleListReponse?.modules?.length === 0}
                  className="p-2.5 hover:bg-purple-100 disabled:bg-gray-100 disabled:cursor-not-allowed rounded-xl transition-all duration-200 hover:scale-105"
                  title="Reorder Modules"
                >
                  <GripVertical size={20} className="text-purple-600 disabled:text-gray-400" />
                </button>
                <button
                  onClick={() => navigate(`/module?courseId=${courseId}`)}
                  className="p-2.5 hover:bg-indigo-100 rounded-xl transition-all duration-200 hover:scale-105"
                  title="Add New Module"
                >
                  <PlusCircle size={20} className="text-indigo-600" />
                </button>
                <button
                  onClick={() => setIsSidebarVisible(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 ml-2"
                >
                  <ChevronLeft size={20} className="text-gray-600" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto h-[calc(100%-80px)] p-2">
              {moduleListReponse?.modules?.map((module) => (
                <div
                  key={module.id}
                  onClick={() => selectModule(module)}
                  className={`cursor-pointer p-4 rounded-xl transition-all duration-200 mb-2 group relative ${
                    selectedModule?.id === module.id
                      ? "bg-gradient-to-r from-indigo-500 to-blue-500 text-white shadow-lg transform scale-[1.02]"
                      : "hover:bg-gray-50 hover:shadow-md hover:scale-[1.01]"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      selectedModule?.id === module.id
                        ? "bg-white/20"
                        : "bg-indigo-100 group-hover:bg-indigo-200"
                    }`}>
                      <BookOpen size={18} className={
                        selectedModule?.id === module.id
                          ? "text-white"
                          : "text-indigo-600"
                      } />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold mb-1 ${
                        selectedModule?.id === module.id
                          ? "text-white"
                          : "text-gray-800"
                      }`}>
                        {module.title}
                      </div>
                      <div className={`text-xs ${
                        selectedModule?.id === module.id
                          ? "text-white/80"
                          : "text-gray-500"
                      }`}>
                        {module.chapterCount} Chapters{module.length && ` • ${module.length}`}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => handleEditModule(e, module)}
                        className={`p-1.5 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100 ${
                          selectedModule?.id === module.id
                            ? "bg-white/20 hover:bg-white/30 text-white"
                            : "bg-indigo-100 hover:bg-indigo-200 text-indigo-600"
                        }`}
                        title="Edit Module"
                        type="button"
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDeleteModule(e, module)}
                        className={`p-1.5 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100 ${
                          selectedModule?.id === module.id
                            ? "bg-red-500/20 hover:bg-red-500/30 text-white"
                            : "bg-red-50 hover:bg-red-100 text-red-500"
                        }`}
                        title="Delete Module"
                        type="button"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isSidebarVisible && (
          <div
            className="cursor-col-resize w-1 bg-gradient-to-b from-gray-200 to-gray-300 hover:from-indigo-200 hover:to-blue-300 transition-all duration-200"
            onMouseDown={handleMouseDown}
          />
        )}

        {!isSidebarVisible && (
          <button
            onClick={() => setIsSidebarVisible(true)}
            className="absolute top-6 left-4 bg-white shadow-lg rounded-xl p-3 hover:bg-gray-50 hover:shadow-xl transition-all duration-200 z-20 border border-gray-200 hover:scale-105"
          >
            <ChevronRight size={20} className="text-indigo-600" />
          </button>
        )}

        <div className="flex-1 bg-gradient-to-br from-gray-50 via-white to-indigo-50 overflow-y-auto">
          <div className="p-8">
            <div className="mb-6 text-center">
              <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-2xl shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/90 via-purple-600/90 to-blue-600/90"></div>
                <div className="relative px-6 py-4 text-center">
                  <div className="inline-flex items-center gap-3 mb-2">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                      <BookOpen size={20} className="text-white" />
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-white mb-1">
                        {course?.name || "Course"}{course?.length && ` • ${course.length}`}
                      </h1>
                      <h2 className="text-sm font-medium text-white/90 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-white/60 rounded-full"></div>
                        {selectedModule?.title || "Select a Module"}
                      </h2>
                    </div>
                  </div>
                </div>
                <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-8 translate-x-8"></div>
                <div className="absolute bottom-0 left-0 w-12 h-12 bg-white/10 rounded-full translate-y-6 -translate-x-6"></div>
              </div>
            </div>

          {isLoadingChapters ? (
            <div className="flex justify-center items-center h-64">
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <CircularProgress size={60} thickness={4} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <BookOpen size={24} className="text-indigo-600" />
                  </div>
                </div>
                <p className="text-gray-600 font-medium">Loading chapters...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {chapters?.length > 0 ? (
                chapters.map((chapter, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4, delay: idx * 0.1 }}
                    className="relative group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-100 hover:border-indigo-200 hover:-translate-y-2"
                    onClick={() => handleChapterClick(chapter)}
                  >
                    {/* Order Index Circle */}
                    <div className="absolute top-3 left-3 z-10">
                      <div className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center border border-gray-200">
                        <span className="text-sm font-semibold text-gray-700">
                          {chapter.orderIndex || idx + 1}
                        </span>
                      </div>
                    </div>

                    {/* Settings Dropdown Button - Top Right */}
                    <div className="absolute top-3 right-3 z-10">
                      <button
                        onClick={(e) => handleMenuOpen(e, chapter)}
                        className="p-2 bg-white rounded-full shadow-md border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                        title="Chapter Settings"
                        type="button"
                      >
                        <MoreVertical size={18} className="text-gray-600" />
                      </button>
                    </div>
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-xl">
                            {getIconByChapterType(chapter.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-gray-800 mb-1 line-clamp-2 leading-tight">
                              {chapter.title}
                            </h3>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span className="px-2 py-1 bg-gray-100 rounded-full font-medium">
                                {chapter.type}
                              </span>
                              {chapter.length && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                                  {chapter.length}
                                </span>
                              )}
                              {chapter.type === "VIDEO" && chapter.videoUploadStatusCode !== undefined && (
                                <div 
                                  className="flex items-center"
                                  title={getVideoUploadStatusTooltip(chapter.videoUploadStatusCode)}
                                >
                                  {getVideoUploadStatusIcon(chapter.videoUploadStatusCode)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Link2
                            size={18}
                            className="text-gray-400 group-hover:text-indigo-500 transition-colors"
                          />
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed">
                        {chapter.description}
                      </p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-blue-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                  </motion.div>
                ))
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center py-16">
                  <div className="p-6 bg-gray-50 rounded-2xl mb-4">
                    <BookOpen size={48} className="text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No chapters yet</h3>
                  <p className="text-gray-500 text-center max-w-md">
                    This module doesn't have any chapters. Add your first chapter to get started.
                  </p>
                </div>
              )}

              {/* Chapter Settings Menu Dropdown */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    minWidth: 200,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  }
                }}
              >
                {selectedChapterForMenu?.type === 'ARTICLE' && (
                  <MenuItem 
                    onClick={handleMenuUpdateArticle}
                    sx={{ 
                      py: 1.5, 
                      px: 2,
                      '&:hover': { bgcolor: 'purple.50' }
                    }}
                  >
                    <BookOpen size={18} className="mr-3 text-purple-500" />
                    <span className="text-sm font-medium">Update Article</span>
                  </MenuItem>
                )}
                {selectedChapterForMenu?.type === 'VIDEO' && (
                  <MenuItem 
                    onClick={handleMenuReuploadVideo}
                    sx={{ 
                      py: 1.5, 
                      px: 2,
                      '&:hover': { bgcolor: 'green.50' }
                    }}
                  >
                    <Upload size={18} className="mr-3 text-green-500" />
                    <span className="text-sm font-medium">Reupload Video</span>
                  </MenuItem>
                )}
                {selectedChapterForMenu?.type === 'VIDEO' && (
                  <MenuItem 
                    onClick={handleMenuUploadThumbnail}
                    sx={{ 
                      py: 1.5, 
                      px: 2,
                      '&:hover': { bgcolor: 'indigo.50' }
                    }}
                  >
                    <Image size={18} className="mr-3 text-indigo-500" />
                    <span className="text-sm font-medium">Upload Thumbnail</span>
                  </MenuItem>
                )}
                {selectedChapterForMenu?.type === 'VIDEO' && (
                  <MenuItem 
                    onClick={handleMenuAddCaption}
                    sx={{ 
                      py: 1.5, 
                      px: 2,
                      '&:hover': { bgcolor: 'orange.50' }
                    }}
                  >
                    <Subtitles size={18} className="mr-3 text-orange-500" />
                    <span className="text-sm font-medium">Add Caption</span>
                  </MenuItem>
                )}
                {selectedChapterForMenu?.type === 'QUIZ' && (
                  <MenuItem 
                    onClick={handleMenuUpdateQuiz}
                    sx={{ 
                      py: 1.5, 
                      px: 2,
                      '&:hover': { bgcolor: 'blue.50' }
                    }}
                  >
                    <HelpCircle size={18} className="mr-3 text-blue-500" />
                    <span className="text-sm font-medium">Update Quiz</span>
                  </MenuItem>
                )}
                <MenuItem 
                  onClick={handleMenuEditChapter}
                  sx={{ 
                    py: 1.5, 
                    px: 2,
                    '&:hover': { bgcolor: 'blue.50' }
                  }}
                >
                  <Edit3 size={18} className="mr-3 text-blue-500" />
                  <span className="text-sm font-medium">Edit Chapter</span>
                </MenuItem>
                <MenuItem 
                  onClick={handleMenuDelete}
                  sx={{ 
                    py: 1.5, 
                    px: 2,
                    '&:hover': { bgcolor: 'red.50' }
                  }}
                >
                  <Trash2 size={18} className="mr-3 text-red-500" />
                  <span className="text-sm font-medium">Delete</span>
                </MenuItem>
              </Menu>

              <div className="col-span-full flex justify-center gap-4 mt-8">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setReorderDialogOpen(true)}
                  disabled={chapters?.length === 0}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white px-6 py-4 rounded-2xl shadow-lg hover:shadow-xl flex items-center gap-3 transition-all duration-300 font-semibold"
                  title="Reorder Chapters"
                >
                  <GripVertical size={24} />
                  Reorder Chapters
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(`/chapter/${selectedModule?.id}`)}
                  className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white px-8 py-4 rounded-2xl shadow-lg hover:shadow-xl flex items-center gap-3 transition-all duration-300 font-semibold"
                  title="Add Chapter"
                >
                  <PlusCircle size={24} />
                  Add New Chapter
                </motion.button>
              </div>
            </div>
          )}
          </div>
        </div>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '1.2rem',
          }}>
            <div className="flex items-center gap-3">
              {selectedChapter?.type === "VIDEO" ? (
                <PlayCircle size={24} />
              ) : (
                <Info size={24} />
              )}
              {selectedChapter?.title}
            </div>
            <IconButton 
              onClick={() => setOpenDialog(false)}
              sx={{ 
                color: 'white',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.1)' 
                }
              }}
            >
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, '&.MuiDialogContent-root': { padding: 0 } }}>
            {selectedChapter?.type === "VIDEO" ? (
              <Box>
                <Box sx={{ 
                  width: '100%', 
                  height: '500px',
                  backgroundColor: '#000',
                  borderRadius: '0 0 16px 16px',
                  overflow: 'hidden',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {selectedChapter?.videoUrl ? (
                    <iframe
                      src={selectedChapter.videoUrl}
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        border: 'none',
                        borderRadius: '0 0 16px 16px',
                        display: 'block',
                        objectFit: 'cover'
                      }}
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      title={selectedChapter.title}
                      frameBorder="0"
                      scrolling="no"
                    />
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '100%',
                      color: 'white',
                      flexDirection: 'column',
                      gap: 2,
                    }}>
                      <PlayCircle size={48} />
                      <Typography variant="h6">Video not available</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        The video content is not yet uploaded or processed.
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body1" sx={{ 
                    color: '#64748b',
                    lineHeight: 1.6,
                    fontSize: '1rem',
                  }}>
                    {selectedChapter?.description}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ 
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontSize: '1rem',
                }}>
                  {selectedChapter?.description}
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '1.2rem',
          }}>
            <div className="flex items-center gap-3">
              <Trash2 size={24} />
              Delete Chapter
            </div>
            <IconButton 
              onClick={handleDeleteCancel}
              sx={{ 
                color: 'white',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.1)' 
                }
              }}
            >
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#64748b',
              lineHeight: 1.6,
              fontSize: '1rem',
              mb: 2,
            }}>
              Are you sure you want to delete the chapter "{chapterToDelete?.title}"? This action cannot be undone.
            </Typography>
            <div className="flex gap-3 justify-end mt-4">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Article Dialog */}
        <Dialog
          open={deleteArticleDialogOpen}
          onClose={handleDeleteArticleCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            borderRadius: '16px 16px 0 0',
            px: 3,
            py: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Trash2 size={24} />
              <span style={{ fontWeight: 600, fontSize: '1.25rem' }}>
                🗑️ Delete Article Content
              </span>
            </Box>
            <IconButton onClick={handleDeleteArticleCancel} sx={{ color: 'white' }}>
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#64748b',
              lineHeight: 1.6,
              fontSize: '1rem',
              mb: 2,
            }}>
              Are you sure you want to delete the article content for <strong>"{chapterToDeleteArticle?.title}"</strong>?
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#ef4444',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}>
              ⚠️ This action cannot be undone. The article content will be permanently removed.
            </Typography>
          </DialogContent>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            p: 3,
            borderTop: '1px solid #e5e7eb'
          }}>
            <Button onClick={handleDeleteArticleCancel} variant="outlined" disabled={deleteArticleLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteArticleConfirm}
              variant="contained"
              sx={{
                backgroundColor: '#ef4444',
                '&:hover': {
                  backgroundColor: '#dc2626',
                },
              }}
              disabled={deleteArticleLoading}
            >
              {deleteArticleLoading ? 'Deleting...' : 'Delete Article'}
            </Button>
          </Box>
        </Dialog>

        {/* Delete Quiz Dialog */}
        <Dialog
          open={deleteQuizDialogOpen}
          onClose={handleDeleteQuizCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            borderRadius: '16px 16px 0 0',
            px: 3,
            py: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Trash2 size={24} />
              <span style={{ fontWeight: 600, fontSize: '1.25rem' }}>
                🗑️ Delete Quiz Content
              </span>
            </Box>
            <IconButton onClick={handleDeleteQuizCancel} sx={{ color: 'white' }}>
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#64748b',
              lineHeight: 1.6,
              fontSize: '1rem',
              mb: 2,
            }}>
              Are you sure you want to delete all quiz content for <strong>"{chapterToDeleteQuiz?.title}"</strong>?
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#ef4444',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}>
              ⚠️ This action cannot be undone. All quiz questions and answers will be permanently removed.
            </Typography>
          </DialogContent>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            p: 3,
            borderTop: '1px solid #e5e7eb'
          }}>
            <Button onClick={handleDeleteQuizCancel} variant="outlined" disabled={loadingDeleteAllQuizzes}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteQuizConfirm}
              variant="contained"
              sx={{
                backgroundColor: '#ef4444',
                '&:hover': {
                  backgroundColor: '#dc2626',
                },
              }}
              disabled={loadingDeleteAllQuizzes}
            >
              {loadingDeleteAllQuizzes ? 'Deleting...' : 'Delete Quiz'}
            </Button>
          </Box>
        </Dialog>

        {/* Reorder Chapters Dialog */}
        <Dialog
          open={reorderDialogOpen}
          onClose={() => setReorderDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
            }
          }}
        >
          <DialogContent sx={{ p: 0, '&.MuiDialogContent-root': { padding: 0 } }}>
            <ReOrderChapter
              chapters={chapters}
              onClose={() => setReorderDialogOpen(false)}
              onSuccess={handleReorderSuccess}
            />
          </DialogContent>
        </Dialog>

        {/* Reorder Module Dialog */}
        <Dialog
          open={reorderModuleDialogOpen}
          onClose={() => setReorderModuleDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
            }
          }}
        >
          <DialogContent sx={{ p: 0, '&.MuiDialogContent-root': { padding: 0 } }}>
            <ReOrderModule
              modules={moduleListReponse?.modules || []}
              onClose={() => setReorderModuleDialogOpen(false)}
              onSuccess={handleReorderModuleSuccess}
            />
          </DialogContent>
        </Dialog>

        {/* Reupload Video Dialog */}
        <Dialog
          open={reuploadDialogOpen}
          onClose={() => setReuploadDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1,
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ fontWeight: 600, color: '#1f2937', fontSize: '1.25rem' }}>
              Reupload Video
            </span>
            <IconButton
              onClick={() => setReuploadDialogOpen(false)}
              sx={{
                color: '#6b7280',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.1)' 
                }
              }}
            >
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#64748b',
              lineHeight: 1.6,
              fontSize: '1rem',
              mb: 3,
            }}>
              Select a new video file to replace the current video for "{selectedChapterForReupload?.title}".
            </Typography>
            
            {/* File Upload Area */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleVideoUpload(file);
                  }
                }}
                className="hidden"
                id="reupload-video-input"
                disabled={isUploading}
              />
              <label
                htmlFor="reupload-video-input"
                className={`cursor-pointer flex flex-col items-center ${
                  isUploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Upload size={48} className="text-gray-400 mb-2" />
                <Typography variant="body1" sx={{ color: '#374151', mb: 1 }}>
                  {isUploading ? 'Uploading...' : 'Click to select video file'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Supports MP4, AVI, MOV, and other video formats
                </Typography>
              </label>
            </div>

            {/* Upload Progress */}
            {isUploading && (
              <Box sx={{ 
                mt: 3, 
                p: 3, 
                backgroundColor: 'rgba(102, 126, 234, 0.1)', 
                borderRadius: 3,
                border: '1px solid rgba(102, 126, 234, 0.2)',
              }}>
                <Typography variant="body1" sx={{ color: '#667eea', fontWeight: '600', mb: 2 }}>
                  📤 Uploading video... {Math.round(uploadProgress)}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  sx={{ 
                    height: 12, 
                    borderRadius: 6,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 6,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    }
                  }} 
                />
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Update Article Dialog */}
        <Dialog
          open={updateArticleDialogOpen}
          onClose={handleArticleCancel}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1,
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ fontWeight: 600, color: '#7c3aed', fontSize: '1.25rem' }}>
              📝 Update Article
            </span>
            <IconButton
              onClick={handleArticleCancel}
              sx={{
                color: '#6b7280',
                '&:hover': { 
                  backgroundColor: 'rgba(124, 58, 237, 0.1)' 
                }
              }}
            >
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#374151',
              lineHeight: 1.6,
              fontSize: '1rem',
              mb: 3,
            }}>
              Update the article content for "{selectedChapterForArticle?.title}".
            </Typography>
            
            
            {/* ReactQuill Editor */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ 
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '2px solid #7c3aed',
                overflow: 'hidden',
                minHeight: '350px'
              }}>
                <ReactQuill
                  value={articleContent || ''}
                  onChange={handleArticleContentChange}
                  theme="snow"
                  style={{ 
                    minHeight: "300px",
                    backgroundColor: 'white'
                  }}
                  readOnly={updateArticleLoading || articleByChapterIdLoading}
                  placeholder="Enter article content here..."
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      [{ font: [] }],
                      ["bold", "italic", "underline", "strike", "blockquote"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ indent: "-1" }, { indent: "+1" }],
                      ["link", "image"],
                      ["clean"],
                    ],
                  }}
                  formats={[
                    'header', 'font', 'size',
                    'bold', 'italic', 'underline', 'strike', 'blockquote',
                    'list', 'bullet', 'indent',
                    'link', 'image', 'color', 'background'
                  ]}
                />
              </div>
              
              {/* Debug info */}
              <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                ReactQuill Debug: Content length: {articleContent?.length || 0} | 
                Loading: {articleByChapterIdLoading ? 'Yes' : 'No'} |
                Update Loading: {updateArticleLoading ? 'Yes' : 'No'}
              </div>
            </div>
            
            {articleByChapterIdLoading && (
              <Box sx={{ 
                mt: 3, 
                p: 3, 
                backgroundColor: 'rgba(124, 58, 237, 0.1)', 
                borderRadius: 3,
                border: '1px solid rgba(124, 58, 237, 0.2)',
              }}>
                <Typography variant="body1" sx={{ color: '#7c3aed', fontWeight: '600', mb: 2 }}>
                  📖 Loading article content...
                </Typography>
                <LinearProgress 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                    }
                  }} 
                />
              </Box>
            )}
            
            {updateArticleLoading && (
              <Box sx={{ 
                mt: 3, 
                p: 3, 
                backgroundColor: 'rgba(124, 58, 237, 0.1)', 
                borderRadius: 3,
                border: '1px solid rgba(124, 58, 237, 0.2)',
              }}>
                <Typography variant="body1" sx={{ color: '#7c3aed', fontWeight: '600', mb: 2 }}>
                  💾 Updating article...
                </Typography>
                <LinearProgress 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                    }
                  }} 
                />
              </Box>
            )}
          </DialogContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            p: 3, 
            pt: 0,
            borderTop: '1px solid #e5e7eb'
          }}>
            <Button
              onClick={handleArticleCancel}
              disabled={updateArticleLoading || articleByChapterIdLoading}
              sx={{
                color: '#6b7280',
                borderColor: '#d1d5db',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: 'rgba(107, 114, 128, 0.05)'
                },
                '&:disabled': {
                  color: '#9ca3af',
                  borderColor: '#e5e7eb'
                }
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleArticleSave}
              disabled={updateArticleLoading || articleByChapterIdLoading || !articleContent || articleContent.trim() === '' || articleContent === '<p><br></p>'}
              sx={{
                backgroundColor: '#7c3aed',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#6d28d9',
                },
                '&:disabled': {
                  backgroundColor: '#d1d5db',
                  color: '#9ca3af'
                }
              }}
              variant="contained"
            >
              {updateArticleLoading ? 'Updating...' : articleByChapterIdLoading ? 'Loading...' : 'Update Article'}
            </Button>
          </Box>
        </Dialog>

        {/* Confirmation Dialog for Reupload */}
        <Dialog
          open={confirmReuploadDialogOpen}
          onClose={handleCancelReupload}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1,
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ fontWeight: 600, color: '#f59e0b', fontSize: '1.25rem' }}>
              ⚠️ Confirm Video Reupload
            </span>
            <IconButton
              onClick={handleCancelReupload}
              sx={{
                color: '#6b7280',
                '&:hover': { 
                  backgroundColor: 'rgba(245, 158, 11, 0.1)' 
                }
              }}
            >
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#374151',
              lineHeight: 1.6,
              fontSize: '1rem',
              mb: 2,
            }}>
              Are you sure you want to reupload the video for "{chapterToReupload?.title}"?
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#6b7280',
              lineHeight: 1.5,
              fontSize: '0.9rem',
            }}>
              This will replace the current video file. The upload process may take some time depending on the file size.
            </Typography>
          </DialogContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            p: 3, 
            pt: 0,
            borderTop: '1px solid #e5e7eb'
          }}>
            <Button
              onClick={handleCancelReupload}
              sx={{
                color: '#6b7280',
                borderColor: '#d1d5db',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: 'rgba(107, 114, 128, 0.05)'
                }
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReupload}
              sx={{
                backgroundColor: '#f59e0b',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#d97706',
                }
              }}
              variant="contained"
            >
              Confirm Reupload
            </Button>
          </Box>
        </Dialog>

        {/* Success Dialog */}
        <Dialog
          open={successDialogOpen}
          onClose={() => setSuccessDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1,
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ fontWeight: 600, color: '#059669', fontSize: '1.25rem' }}>
              ✅ Operation Successful
            </span>
            <IconButton
              onClick={() => setSuccessDialogOpen(false)}
              sx={{
                color: '#6b7280',
                '&:hover': { 
                  backgroundColor: 'rgba(5, 150, 105, 0.1)' 
                }
              }}
            >
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#374151',
              lineHeight: 1.6,
              fontSize: '1rem',
            }}>
              {errorMessage}
            </Typography>
          </DialogContent>
        </Dialog>

        {/* Error Dialog */}
        <Dialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1,
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ fontWeight: 600, color: '#dc2626', fontSize: '1.25rem' }}>
              ❌ Operation Error
            </span>
            <IconButton
              onClick={() => setErrorDialogOpen(false)}
              sx={{
                color: '#6b7280',
                '&:hover': { 
                  backgroundColor: 'rgba(220, 38, 38, 0.1)' 
                }
              }}
            >
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#dc2626',
              lineHeight: 1.6,
              fontSize: '1rem',
            }}>
              {errorMessage}
            </Typography>
          </DialogContent>
        </Dialog>

        {/* Update Quiz Dialog - Multiple Questions */}
        <Dialog
          open={updateQuizDialogOpen}
          onClose={handleQuizUpdateCancel}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              maxHeight: '90vh',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            fontWeight: 600,
            fontSize: '1.25rem',
            borderRadius: '16px 16px 0 0',
            p: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <HelpCircle size={24} />
              <span style={{ fontWeight: 600, fontSize: '1.25rem' }}>
                🧠 Update Quiz Questions ({quizzes.length} questions)
              </span>
            </Box>
            <IconButton onClick={handleQuizUpdateCancel} sx={{ color: 'white' }}>
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#374151', 
              fontSize: '1rem', 
              lineHeight: 1.6,
              mb: 3
            }}>
              Update quiz content for <strong>"{selectedChapterForQuiz?.title}"</strong>
            </Typography>

            {/* Question Navigation */}
            {quizzes.length > 1 && (
              <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {quizzes.map((_, index) => (
                  <Button
                    key={index}
                    onClick={() => navigateToQuestion(index)}
                    variant={currentQuestionIndex === index ? "contained" : "outlined"}
                    size="small"
                    sx={{
                      minWidth: '40px',
                      ...(currentQuestionIndex === index && {
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white'
                      })
                    }}
                  >
                    Q{index + 1}
                  </Button>
                ))}
                <Button
                  onClick={addNewQuestion}
                  variant="outlined"
                  size="small"
                  startIcon={<Plus size={16} />}
                  sx={{ color: '#10b981', borderColor: '#10b981' }}
                >
                  Add Question
                </Button>
              </Box>
            )}

            {/* Current Question Form */}
            {quizzes.length > 0 && getCurrentQuiz() && (
              <Box sx={{ 
                p: 3, 
                backgroundColor: '#f8fafc', 
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}>
                {/* Question Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                    Question {currentQuestionIndex + 1} of {quizzes.length}
                  </Typography>
                  {quizzes.length > 1 && (
                    <Button
                      onClick={deleteCurrentQuestion}
                      variant="outlined"
                      size="small"
                      startIcon={<Trash2 size={16} />}
                      sx={{ color: '#ef4444', borderColor: '#ef4444' }}
                    >
                      Delete Question
                    </Button>
                  )}
                </Box>

                {/* Question */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151', fontWeight: 600 }}>
                    Question
                  </Typography>
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="Enter your question (max 512 characters)"
                    value={getCurrentQuiz().question || ""}
                    onChange={(e) => updateCurrentQuiz('question', e.target.value.slice(0, 512))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'white',
                      }
                    }}
                  />
                </Box>

                {/* Answer Type */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151', fontWeight: 600 }}>
                    Answer Type:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <input
                        type="radio"
                        name={`answerType-${currentQuestionIndex}`}
                        checked={!getCurrentQuiz().multipleAnswers}
                        onChange={() => {
                          updateCurrentQuiz('multipleAnswers', false);
                          // Clear all correct answers
                          ['optionACorrect', 'optionBCorrect', 'optionCCorrect', 'optionDCorrect', 'optionECorrect'].forEach(field => {
                            updateCurrentQuiz(field, false);
                          });
                        }}
                      />
                      <Typography variant="body2">Single Correct Answer</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <input
                        type="radio"
                        name={`answerType-${currentQuestionIndex}`}
                        checked={getCurrentQuiz().multipleAnswers}
                        onChange={() => {
                          updateCurrentQuiz('multipleAnswers', true);
                        }}
                      />
                      <Typography variant="body2">Multiple Correct Answers</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Options */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151', fontWeight: 600 }}>
                    Options:
                  </Typography>
                  {['A', 'B', 'C', 'D', 'E'].map((optionLetter, i) => {
                    const optionValue = getCurrentQuiz()[`option${optionLetter}`] || "";
                    const isCorrect = getCurrentQuiz()[`option${optionLetter}Correct`] || false;
                    
                    if (i > 0 && !optionValue && !getCurrentQuiz()[`option${String.fromCharCode(65 + i - 1)}`]) {
                      return null; // Don't show empty options beyond the last filled one
                    }
                    
                    return (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 2,
                          p: 2,
                          backgroundColor: 'white',
                          borderRadius: 2,
                          border: '1px solid #e2e8f0'
                        }}
                      >
                        <input
                          type={getCurrentQuiz().multipleAnswers ? "checkbox" : "radio"}
                          name={`correctOption-${currentQuestionIndex}`}
                          checked={isCorrect}
                          onChange={() => handleCorrectChange(i)}
                        />
                        <TextField
                          fullWidth
                          placeholder={`Option ${optionLetter}`}
                          value={optionValue}
                          onChange={(e) => handleOptionChange(i, e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                        {i >= 2 && (
                          <IconButton
                            onClick={() => deleteOption(i)}
                            sx={{ color: '#ef4444' }}
                            title="Remove Option"
                          >
                            <Trash2 size={20} />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}

                  {['A', 'B', 'C', 'D', 'E'].filter((letter, i) => {
                    const hasValue = getCurrentQuiz()[`option${letter}`];
                    const hasPrevious = i === 0 || getCurrentQuiz()[`option${String.fromCharCode(65 + i - 1)}`];
                    return !hasValue && hasPrevious;
                  }).length > 0 && ['A', 'B', 'C', 'D', 'E'].filter(letter => getCurrentQuiz()[`option${letter}`]).length < 5 && (
                    <Button
                      onClick={addOption}
                      startIcon={<Plus size={20} />}
                      sx={{
                        color: '#3b82f6',
                        fontWeight: 600,
                        mt: 1,
                        '&:hover': {
                          backgroundColor: '#f0f9ff'
                        }
                      }}
                    >
                      Add More Option
                    </Button>
                  )}
                </Box>

                {/* Explanation */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151', fontWeight: 600 }}>
                    Explanation (Optional):
                  </Typography>
                  <TextField
                    multiline
                    rows={2}
                    fullWidth
                    placeholder="Add explanation (optional, max 512 characters)"
                    value={getCurrentQuiz().explanation || ""}
                    onChange={(e) => updateCurrentQuiz('explanation', e.target.value.slice(0, 512))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'white',
                      }
                    }}
                  />
                </Box>

                {/* Validation Error */}
                {validationError && (
                  <Typography variant="body2" sx={{ 
                    color: '#ef4444', 
                    textAlign: 'center', 
                    fontWeight: 600, 
                    mt: 2 
                  }}>
                    {validationError}
                  </Typography>
                )}
              </Box>
            )}

            {/* No Questions State */}
            {quizzes.length === 0 && (
              <Box sx={{ 
                p: 6, 
                textAlign: 'center',
                backgroundColor: '#f8fafc', 
                borderRadius: 2,
                border: '2px dashed #e2e8f0'
              }}>
                <Typography variant="h6" sx={{ color: '#6b7280', mb: 2 }}>
                  No quiz questions found
                </Typography>
                <Button
                  onClick={addNewQuestion}
                  variant="contained"
                  startIcon={<Plus size={20} />}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    }
                  }}
                >
                  Add First Question
                </Button>
              </Box>
            )}
          </DialogContent>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 3,
            borderTop: '1px solid #e5e7eb'
          }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {quizzes.length > 1 && (
                <>
                  <Button
                    onClick={() => navigateToQuestion(currentQuestionIndex - 1)}
                    disabled={currentQuestionIndex === 0}
                    variant="outlined"
                    size="small"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={() => navigateToQuestion(currentQuestionIndex + 1)}
                    disabled={currentQuestionIndex === quizzes.length - 1}
                    variant="outlined"
                    size="small"
                  >
                    Next
                  </Button>
                </>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                onClick={handleQuizUpdateCancel} 
                variant="outlined"
                disabled={loadingUpsertQuizzes}
                sx={{
                  borderColor: '#d1d5db',
                  color: '#6b7280',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f9fafb'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleQuizUpdateConfirm}
                variant="contained"
                disabled={loadingUpsertQuizzes || loadingQuizzesByChapter || quizzes.length === 0}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white',
                  fontWeight: 600,
                  px: 4,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                  }
                }}
              >
                {loadingUpsertQuizzes ? 'Updating...' : loadingQuizzesByChapter ? 'Loading...' : `Update ${quizzes.length} Questions`}
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* Edit Module Dialog */}
        <Dialog
          open={editModuleDialogOpen}
          onClose={handleModuleUpdateCancel}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1,
            borderBottom: '1px solid #e5e7eb'
          }}>
            <span style={{ fontWeight: 600, color: '#6366f1', fontSize: '1.25rem' }}>
              📝 Edit Module
            </span>
            <IconButton
              onClick={handleModuleUpdateCancel}
              sx={{
                color: '#6b7280',
                '&:hover': { 
                  backgroundColor: 'rgba(99, 102, 241, 0.1)' 
                }
              }}
            >
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#374151',
              lineHeight: 1.6,
              fontSize: '1rem',
              mb: 3,
            }}>
              Update the module information below.
            </Typography>
            
            {/* Module Name Field */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#374151', fontWeight: 600 }}>
                Module Name *
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter module name"
                value={editModuleName}
                onChange={(e) => setEditModuleName(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                  }
                }}
              />
            </Box>

            {/* Module Description Field */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#374151', fontWeight: 600 }}>
                Module Description
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Enter module description (optional)"
                value={editModuleDescription}
                onChange={(e) => setEditModuleDescription(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                  }
                }}
              />
            </Box>
            
            {updateModuleLoading && (
              <Box sx={{ 
                mt: 3, 
                p: 3, 
                backgroundColor: 'rgba(99, 102, 241, 0.1)', 
                borderRadius: 3,
                border: '1px solid rgba(99, 102, 241, 0.2)',
              }}>
                <Typography variant="body1" sx={{ color: '#6366f1', fontWeight: '600', mb: 2 }}>
                  💾 Updating module...
                </Typography>
                <LinearProgress 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    }
                  }} 
                />
              </Box>
            )}
          </DialogContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            p: 3, 
            pt: 0,
            borderTop: '1px solid #e5e7eb'
          }}>
            <Button
              onClick={handleModuleUpdateCancel}
              disabled={updateModuleLoading}
              sx={{
                color: '#6b7280',
                borderColor: '#d1d5db',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: 'rgba(107, 114, 128, 0.05)'
                },
                '&:disabled': {
                  color: '#9ca3af',
                  borderColor: '#e5e7eb'
                }
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleModuleUpdateSave}
              disabled={updateModuleLoading || !editModuleName.trim()}
              sx={{
                backgroundColor: '#6366f1',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#4f46e5',
                },
                '&:disabled': {
                  backgroundColor: '#d1d5db',
                  color: '#9ca3af'
                }
              }}
              variant="contained"
            >
              {updateModuleLoading ? 'Updating...' : 'Update Module'}
            </Button>
          </Box>
        </Dialog>

        {/* Delete Module Confirmation Dialog */}
        <Dialog
          open={deleteModuleDialogOpen}
          onClose={handleDeleteModuleCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '1.2rem',
          }}>
            <div className="flex items-center gap-3">
              <Trash2 size={24} />
              Delete Module
            </div>
            <IconButton 
              onClick={handleDeleteModuleCancel}
              sx={{ 
                color: 'white',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.1)' 
                }
              }}
            >
              <XCircle size={24} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ 
              color: '#64748b',
              lineHeight: 1.6,
              fontSize: '1rem',
              mb: 2,
            }}>
              Are you sure you want to delete the module <strong>"{moduleToDelete?.title}"</strong>?
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#ef4444',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}>
              ⚠️ This action cannot be undone. All chapters within this module will also be affected.
            </Typography>
            
            {deleteModuleLoading && (
              <Box sx={{ 
                mt: 3, 
                p: 3, 
                backgroundColor: 'rgba(239, 68, 68, 0.1)', 
                borderRadius: 3,
                border: '1px solid rgba(239, 68, 68, 0.2)',
              }}>
                <Typography variant="body1" sx={{ color: '#ef4444', fontWeight: '600', mb: 2 }}>
                  🗑️ Deleting module...
                </Typography>
                <LinearProgress 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    }
                  }} 
                />
              </Box>
            )}
          </DialogContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            p: 3,
            borderTop: '1px solid #e5e7eb'
          }}>
            <Button 
              onClick={handleDeleteModuleCancel} 
              variant="outlined"
              disabled={deleteModuleLoading}
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteModuleConfirm}
              variant="contained"
              disabled={deleteModuleLoading}
              sx={{
                backgroundColor: '#ef4444',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#dc2626',
                },
                '&:disabled': {
                  backgroundColor: '#d1d5db',
                  color: '#9ca3af'
                }
              }}
            >
              {deleteModuleLoading ? 'Deleting...' : 'Delete Module'}
            </Button>
          </Box>
        </Dialog>

        {/* Thumbnail Upload Dialog */}
        <Dialog
          open={thumbnailUploadDialogOpen}
          onClose={handleThumbnailUploadCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Image size={28} />
            <div>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                Upload Chapter Thumbnail
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {selectedChapterForThumbnail?.title}
              </Typography>
            </div>
          </DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                Select an image file for the chapter thumbnail (Max 5MB, JPG/PNG)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={thumbnailUploading}
                sx={{
                  width: '100%',
                  py: 2,
                  borderRadius: 2,
                  borderColor: '#d1d5db',
                  color: '#6b7280',
                  '&:hover': {
                    borderColor: '#6366f1',
                    backgroundColor: 'rgba(99, 102, 241, 0.05)',
                  }
                }}
              >
                {thumbnailFile ? thumbnailFile.name : 'Choose Thumbnail Image'}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailFileChange}
                />
              </Button>
              {thumbnailFile && !thumbnailUploading && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f3f4f6', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Selected: <strong>{thumbnailFile.name}</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                    Size: {(thumbnailFile.size / (1024 * 1024)).toFixed(2)} MB
                  </Typography>
                </Box>
              )}
            </Box>
            
            {thumbnailUploading && (
              <Box sx={{ 
                mt: 3, 
                p: 3, 
                backgroundColor: 'rgba(99, 102, 241, 0.1)', 
                borderRadius: 3,
                border: '1px solid rgba(99, 102, 241, 0.2)',
              }}>
                <Typography variant="body1" sx={{ color: '#6366f1', fontWeight: '600', mb: 2 }}>
                  📤 Uploading thumbnail...
                </Typography>
                <LinearProgress 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    }
                  }} 
                />
              </Box>
            )}
          </DialogContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            p: 3, 
            pt: 0,
            borderTop: '1px solid #e5e7eb'
          }}>
            <Button
              onClick={handleThumbnailUploadCancel}
              disabled={thumbnailUploading}
              sx={{
                color: '#6b7280',
                borderColor: '#d1d5db',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: 'rgba(107, 114, 128, 0.05)',
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleThumbnailUpload}
              variant="contained"
              disabled={!thumbnailFile || thumbnailUploading}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
                '&:disabled': {
                  backgroundColor: '#d1d5db',
                  color: '#9ca3af'
                }
              }}
            >
              {thumbnailUploading ? 'Uploading...' : 'Upload Thumbnail'}
            </Button>
          </Box>
        </Dialog>

        {/* Caption Upload Dialog */}
        <Dialog
          open={captionDialogOpen}
          onClose={handleCaptionUploadCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
            color: 'white',
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Subtitles size={28} />
            <div>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                Add Chapter Caption
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {selectedChapterForCaption?.title}
              </Typography>
            </div>
          </DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                Select a caption file for the video chapter (Max 2MB, .vtt or .srt)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={captionUploading}
                sx={{
                  width: '100%',
                  py: 2,
                  borderRadius: 2,
                  borderColor: '#d1d5db',
                  color: '#6b7280',
                  '&:hover': {
                    borderColor: '#f97316',
                    backgroundColor: 'rgba(249, 115, 22, 0.05)',
                  }
                }}
              >
                {captionFile ? captionFile.name : 'Choose Caption File'}
                <VisuallyHiddenInput
                  type="file"
                  accept=".vtt,.srt"
                  onChange={handleCaptionFileChange}
                />
              </Button>
              {captionFile && !captionUploading && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f3f4f6', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Selected: <strong>{captionFile.name}</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                    Size: {(captionFile.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="caption-language-label">Source Language</InputLabel>
                <Select
                  labelId="caption-language-label"
                  value={captionLanguage}
                  label="Source Language"
                  onChange={(e) => setCaptionLanguage(e.target.value)}
                  disabled={captionUploading}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#d1d5db',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f97316',
                    },
                  }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="hi">Hindi</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Caption Label"
                value={captionLabel}
                onChange={(e) => setCaptionLabel(e.target.value)}
                disabled={captionUploading}
                placeholder="e.g., English, Hindi, etc."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#f97316',
                    },
                  }
                }}
                helperText="Enter a display label for this caption track"
              />
            </Box>
            
            {captionUploading && (
              <Box sx={{ 
                mt: 3, 
                p: 3, 
                backgroundColor: 'rgba(249, 115, 22, 0.1)', 
                borderRadius: 3,
                border: '1px solid rgba(249, 115, 22, 0.2)',
              }}>
                <Typography variant="body1" sx={{ color: '#f97316', fontWeight: '600', mb: 2 }}>
                  📝 Uploading caption...
                </Typography>
                <LinearProgress 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                    }
                  }} 
                />
              </Box>
            )}
          </DialogContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2, 
            p: 3, 
            pt: 0,
            borderTop: '1px solid #e5e7eb'
          }}>
            <Button
              onClick={handleCaptionUploadCancel}
              disabled={captionUploading}
              sx={{
                color: '#6b7280',
                borderColor: '#d1d5db',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: 'rgba(107, 114, 128, 0.05)',
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCaptionUpload}
              variant="contained"
              disabled={!captionFile || !captionLabel.trim() || captionUploading}
              sx={{
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                },
                '&:disabled': {
                  backgroundColor: '#d1d5db',
                  color: '#9ca3af'
                }
              }}
            >
              {captionUploading ? 'Uploading...' : 'Upload Caption'}
            </Button>
          </Box>
        </Dialog>
      </div>
    </>
  );
};

export default ModuleList;
